import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Icon from "components/ShellV2/Icon";

/* Searchable single-select dropdown (port of the design's SearchSelect). Use for
   long option lists (meters, members, communities, resources) where a plain
   <select> is hard to scan. options: [{value, label}] or primitives.
   Shares the rec-ms-* look of MultiSelect, with a search box on top. */
const normalize = (s) => String(s).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export default function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  disabled,
}) {
  const norm = useMemo(
    () => options.map((o) => (typeof o === "object" ? o : { value: o, label: String(o) })),
    [options]
  );
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = norm.find((o) => o.value === value);
  const filtered = useMemo(
    () => (q.trim() ? norm.filter((o) => normalize(o.label).includes(normalize(q.trim()))) : norm),
    [norm, q]
  );

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open) {
      setHi(0);
      const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 20);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    setHi(0);
  }, [q]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[hi];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [hi, open]);

  const pick = (o) => {
    onChange?.(o.value);
    setOpen(false);
    setQ("");
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[hi]) pick(filtered[hi]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQ("");
    }
  };

  return (
    <div className="rec-ms" ref={ref}>
      <button
        type="button"
        className={`rec-ms-control${open ? " open" : ""}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className={`${
            selected ? "" : "rec-ms-ph"
          } flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <Icon name="expand_more" />
      </button>
      {open && (
        <div className="rec-ms-menu">
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-[var(--border,#e6eaf0)]">
            <Icon name="search" style={{ fontSize: 16 }} />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent border-0 outline-none text-[13px] text-ink"
            />
          </div>
          <div ref={listRef} className="max-h-[240px] overflow-y-auto overflow-x-hidden">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-[12.5px] text-ink-3">{emptyLabel}</div>
            ) : (
              filtered.map((o, i) => (
                <button
                  type="button"
                  key={o.value}
                  className={`rec-ms-opt${o.value === value ? " sel" : ""}${i === hi ? " hi" : ""}`}
                  onMouseEnter={() => setHi(i)}
                  onClick={() => pick(o)}
                >
                  <span className="rec-ms-lbl overflow-hidden text-ellipsis whitespace-nowrap">
                    {o.label}
                  </span>
                  {o.value === value && <Icon name="check" style={{ fontSize: 15 }} />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

SearchSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.node,
      }),
    ])
  ).isRequired,
  placeholder: PropTypes.node,
  searchPlaceholder: PropTypes.string,
  emptyLabel: PropTypes.node,
  disabled: PropTypes.bool,
};

SearchSelect.defaultProps = {
  value: undefined,
  onChange: undefined,
  placeholder: "Selecionar…",
  searchPlaceholder: "Procurar…",
  emptyLabel: "Sem resultados",
  disabled: false,
};
